import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface AdminMetrics {
  // MRR (Monthly Recurring Revenue)
  mrr: number; // Total MRR from all active SaaS subscriptions
  mrrGrowth: number; // MRR growth percentage vs last month
  
  // Monthly Active Users
  monthlyActiveUsers: number; // Users who logged in this month
  mauGrowth: number; // MAU growth percentage vs last month
  
  // Average Gym Monthly Revenue
  averageGymMonthlyRevenue: number; // Average revenue per gym (SaaS + platform fees)
  totalGymRevenue: number; // Total revenue across all gyms
  
  // Additional metrics
  totalGyms: number;
  activeGyms: number; // Gyms with active Stripe Connect accounts
  totalUsers: number;
  activeSubscriptions: number; // Active SaaS subscriptions
}

export function useAdminMetrics() {
  return useQuery<AdminMetrics>({
    queryKey: ['admin', 'metrics'],
    staleTime: 60_000, // 1 minute
    queryFn: async () => {
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      // Get all companies
      const { data: companies, error: companiesError } = await supabase
        .from('companies')
        .select('id, stripe_connect_account_status, enable_billing');

      if (companiesError) throw companiesError;

      if (!companies || Array.isArray(companies) === false) {
        throw new Error('Failed to fetch companies');
      }

      interface CompanyRow {
        id: string;
        stripe_connect_account_status: string | null;
        enable_billing: boolean | null;
      }

      const companyRows = companies as unknown as CompanyRow[];
      const totalGyms = companyRows.length;
      const activeGyms = companyRows.filter(
        (c) => c.stripe_connect_account_status === 'active' && c.enable_billing !== false
      ).length;

      // Get all users
      const { count: totalUsers } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true });

      // Get monthly active users (users who logged in this month)
      const { count: monthlyActiveUsers } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .gte('last_login_at', currentMonthStart.toISOString());

      // Get monthly active users last month (for growth calculation)
      const { count: lastMonthMAU } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .gte('last_login_at', lastMonthStart.toISOString())
        .lte('last_login_at', lastMonthEnd.toISOString());

      // Calculate MAU growth
      const mauGrowth = lastMonthMAU && lastMonthMAU > 0
        ? ((monthlyActiveUsers || 0) - lastMonthMAU) / lastMonthMAU * 100
        : 0;

      // Get active SaaS subscriptions
      const { data: saasSubscriptions, error: saasError } = await supabase
        .from('saas_subscriptions')
        .select('price, billing_cycle, status')
        .eq('status', 'active');

      if (saasError) throw saasError;

      // Calculate MRR from SaaS subscriptions
      let mrr = 0;
      (saasSubscriptions || []).forEach((sub) => {
        const price = Number(sub.price || 0);
        if (sub.billing_cycle === 'monthly') {
          mrr += price;
        } else if (sub.billing_cycle === 'quarterly') {
          mrr += price / 3;
        } else if (sub.billing_cycle === 'annual') {
          mrr += price / 12;
        }
      });

      // Get MRR from last month (for growth calculation)
      // This is a simplified calculation - in production you'd want to track historical MRR
      const mrrGrowth = 0; // TODO: Implement historical MRR tracking

      // Get active student subscriptions for platform fee calculation
      const { data: activeEnrollments, error: enrollmentsError } = await supabase
        .from('subscription_enrollments')
        .select('id, subscription_id, company_id')
        .eq('status', 'active');

      if (enrollmentsError) throw enrollmentsError;

      // Get subscription details (price + billing cycle)
      // Price lives on company_subscriptions, not subscription_enrollments
      const enrollmentRows = (activeEnrollments || []) as unknown as Array<{
        subscription_id: string | null;
      }>;

      const subscriptionIds = enrollmentRows.map((e) => e.subscription_id).filter((id): id is string => Boolean(id));
      const { data: subscriptions } = subscriptionIds.length > 0
        ? await supabase
            .from('company_subscriptions')
            .select('id, billing_cycle, price')
            .in('id', subscriptionIds)
        : { data: [] };

      const subscriptionRows = (subscriptions || []) as Array<{
        id: string;
        billing_cycle: string;
        price: number | null;
      }>;
      const subscriptionMap = new Map(subscriptionRows.map((s) => [s.id, s]));

      // Calculate total gym revenue (SaaS + platform fees)
      // Platform fee is 5% of subscription price
      let totalGymRevenue = 0;
      enrollmentRows.forEach((enrollment) => {
        const sub = enrollment.subscription_id ? subscriptionMap.get(enrollment.subscription_id) : undefined;
        const price = Number(sub?.price || 0);
        const billingCycle = sub?.billing_cycle;
        
        let monthlyPrice = 0;
        if (billingCycle === 'monthly') {
          monthlyPrice = price;
        } else if (billingCycle === 'quarterly') {
          monthlyPrice = price / 3;
        } else if (billingCycle === 'annual') {
          monthlyPrice = price / 12;
        } else {
          monthlyPrice = price; // Default to monthly
        }

        // Add gym revenue (subscription price) + platform fee (5%)
        totalGymRevenue += monthlyPrice + (monthlyPrice * 0.05);
      });

      // Add SaaS revenue to total
      totalGymRevenue += mrr;

      // Calculate average gym monthly revenue
      const averageGymMonthlyRevenue = activeGyms > 0 ? totalGymRevenue / activeGyms : 0;

      // Get active SaaS subscription count
      const activeSubscriptions = saasSubscriptions?.length || 0;

      return {
        mrr,
        mrrGrowth,
        monthlyActiveUsers: monthlyActiveUsers || 0,
        mauGrowth,
        averageGymMonthlyRevenue,
        totalGymRevenue,
        totalGyms,
        activeGyms,
        totalUsers: totalUsers || 0,
        activeSubscriptions,
      };
    },
  });
}

